/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as PremiumModalActionCreators from '@app/actions/PremiumModalActionCreators';
import styles from '@app/components/modals/components/plutonium/PremiumTrialBanner.module.css';
import {Button} from '@app/components/uikit/button/Button';
import type {UserRecord} from '@app/records/UserRecord';
import GeoIPStore from '@app/stores/GeoIPStore';
import {getFormattedFullDate} from '@app/utils/DateUtils';
import {shouldShowPremiumFeatures} from '@app/utils/PremiumUtils';
import {getFormattedPrice, PricingTier} from '@app/utils/PricingUtils';
import {UserPremiumTypes} from '@fluxer/constants/src/UserConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {CrownIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';

interface PremiumTrialBannerProps {
	currentUser: UserRecord;
	hideUpgradeButton?: boolean;
}

export const PremiumTrialBanner = observer(function PremiumTrialBanner({
	currentUser,
	hideUpgradeButton,
}: PremiumTrialBannerProps) {
	const {t} = useLingui();

	const countryCode = GeoIPStore.countryCode;
	const monthlyPrice = useMemo(() => getFormattedPrice(PricingTier.Monthly, countryCode), [countryCode]);
	const yearlyPrice = useMemo(() => getFormattedPrice(PricingTier.Yearly, countryCode), [countryCode]);

	const handleUpgrade = useCallback(() => {
		PremiumModalActionCreators.open();
	}, []);

	if (!currentUser.isOutOfBandTrialActive() || !shouldShowPremiumFeatures()) return null;

	const expiresAt = currentUser.premiumOutOfBandTrialEndsAt;
	const expiryLabel = expiresAt ? getFormattedFullDate(expiresAt) : null;
	const hasPaidPremium = currentUser.premiumType != null && currentUser.premiumType >= UserPremiumTypes.SUBSCRIPTION;
	const showUpgrade = !hasPaidPremium && !hideUpgradeButton;

	return (
		<div className={styles.banner}>
			<div className={styles.content}>
				<CrownIcon className={styles.icon} weight="fill" />
				<div className={styles.textContainer}>
					<p className={styles.title}>
						{expiryLabel
							? t`You're on a Plutonium trial that expires on ${expiryLabel}`
							: t`You're on a Plutonium trial`}
					</p>
					{showUpgrade && (
						<p className={styles.description}>
							<Trans>
								Upgrade for just {monthlyPrice}/mo or {yearlyPrice}/yr to earn a badge on your profile and keep your
								perks for longer.
							</Trans>
						</p>
					)}
				</div>
				{showUpgrade && (
					<Button variant="inverted" small onClick={handleUpgrade}>
						<Trans>Upgrade</Trans>
					</Button>
				)}
			</div>
		</div>
	);
});
